import React, { useState, FC, ReactElement, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Grid, TextField, Typography, Box } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicNoneIcon from "@mui/icons-material/MicNone";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import {
  addConversations,
  appendChat,
  getChats,
  getSelectedIndexes,
  getconversationid,
} from "../../redux/slices/conversationSlice";
import { getType, getUserFormatPrompt } from "../../redux/slices/typeSlice";
import { getModelDetails } from "../../redux/slices/modelSlice";
import {
  getMetadataFlag,
  getPIIInfo,
  getPromptCompressor,
  getSemanticCache,
  getSendingFullDocument,
  getuser,
  getWebSearch,
} from "../../redux/slices/userSlice";
import { addError } from "../../redux/slices/errorSlice";
import makeHistory from "../../utils/makeHistory";
import apiCall from "../../utils/apiCall";
import Lottie from "lottie-react";
import animationData from "../../assets/typing.json";
import { FIRST_MESSAGE_CHANGE_CONVERSATION_NAME } from "../../endpoint";
import { loadingforconversation } from "../../redux/slices/loadingSlice";
import { getcollection } from "../../redux/slices/collectionSlice";
import { COLLECTION } from "../../data/constants";
import useSpeechToText from "../../hooks/azuretest";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material";

const ChatInput: FC<{ onScroll: () => void }> = ({
  onScroll,
}): ReactElement => {
  const [typing, setTyping] = useState(false);
  const [switchTimer, setSwitchTimer] = useState(0);
  const metadata_flag = useAppSelector(getMetadataFlag);
  const chats = useAppSelector(getChats);
  const chatType = useAppSelector(getType);
  const selectedIndexes = useAppSelector(getSelectedIndexes);
  const current_conversation_id = useAppSelector(getconversationid);
  const user = useAppSelector(getuser);
  const model = useAppSelector(getModelDetails);
  const prompt = useAppSelector(getUserFormatPrompt);
  const collection = useAppSelector(getcollection);
  const dispatch = useAppDispatch();
  const full_doc = useAppSelector(getSendingFullDocument);
  const pii = useAppSelector(getPIIInfo);
  const webSearch = useAppSelector(getWebSearch);
  const semantic_cache = useAppSelector(getSemanticCache);
  const prompt_compressor = useAppSelector(getPromptCompressor);
  const {
    transcript,
    isListening,
    startListening,
    stopListening,
    setTranscript,
    error,
    setError,
  } = useSpeechToText();

  const { accounts } = useMsal();
  const account = accounts[0];

  const data = [
    "Extracting relevant information from sources",
    "LLM getting your response ready",
  ];

  const sendMessage = async (event: any, func = false) => {
    if (
      (event.key === "Enter" && transcript.trim()) ||
      (func && transcript.trim())
    ) {
      if (chatType.geturl !== "" && selectedIndexes.length === 0)
        dispatch(addError("Please select an index"));
      else {
        const message = transcript;
        setTranscript("");
        const chat_hist = makeHistory([...chats], user.chat_history);
        setTyping(true);
        dispatch(
          appendChat({
            message,
            type: "user",
          }),
        );
        onScroll();
        apiCall("POST", chatType.chaturl, {
          query: message,
          chat_hist: chat_hist,
          model: model.semanticEnabled ? "" : model.model,
          temp: user?.temp ?? 1,
          chunks:
            chatType.type === COLLECTION ? collection.files : selectedIndexes,
          chunk_size: user.chunk_size,
          search_type: "similarity",
          conv_id: current_conversation_id,
          user_email: account.username,
          images:
            chatType.type === COLLECTION ? collection.images : selectedIndexes,
          video_names: selectedIndexes,
          name: selectedIndexes[0],
          prompt,
          file_name: selectedIndexes[0],
          db_name: selectedIndexes[0],
          tables: ["all"],
          llm_doc: model.model,
          llm_img: model.imagemodel,
          db_call: false,
          full_doc,
          pii: pii ? true : false,
          web_search: webSearch ? true : false,
          router: model?.semanticEnabled ? true : false,
          semantic_cache: semantic_cache ? true : false,
          prompt_compression: prompt_compressor ? true : false,
          metadata_flag: metadata_flag ? true : false,
        })
          .then(({ result, sources, cypher, org_cypher, creds }) => {
            const responseText = result?.result;
            const responseModel = result?.model;
            if (chats.length === 0) {
              dispatch(loadingforconversation(true));
              apiCall("POST", FIRST_MESSAGE_CHANGE_CONVERSATION_NAME, {
                user_email: account.username,
                conv_id: current_conversation_id,
                question: message,
                answer: responseText,
              })
                .then((res) => {
                  dispatch(addConversations(res?.result));
                })
                .catch((err) => {
                  dispatch(
                    addError(
                      "Failed to load the conversations name. Please try again.",
                    ),
                  );
                })
                .finally(() => {
                  dispatch(loadingforconversation(false));
                });
            }
            dispatch(
              appendChat({
                type: "gpt",
                message: responseText,
                sources,
                model: responseModel,
                //model: model.model,
                chat_type: chatType.shortname,
                cypher,
                org_cypher,
                db_creds: creds,
                image_model:
                  chatType.type === COLLECTION ? model.imagemodel : "",
                collection_chunks: collection?.files,
                collection_images: collection?.images,
                pii,
              }),
            );
            setTyping(false);
            onScroll();
          })
          .catch((err) => {
            console.log("err", err);
            setTyping(false);
            dispatch(
              appendChat({
                type: "gpt",
                message: err,
              }),
            );
          })
          .finally(() => {
            onScroll();
          });
      }
    }
  };

  const typingHandler = (e: any) => {
    setTranscript(e.target.value);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setSwitchTimer((timer) => (timer === 0 ? 1 : 0));
    }, 2000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const theme = useTheme();
  return (
    <Box
      sx={{
        width: "100%",
        mt: 3,
        mb: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden", // Prevent horizontal scrolling
      }}
    >
      {typing && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Lottie animationData={animationData} className="lottie" />
          <Typography fontSize="small" sx={{ color: "gray", ml: 1 }}>
            {data[switchTimer]}
          </Typography>
        </Box>
      )}
      {error && (
        <Box
          sx={{
            background: "#ffd4cc",
            px: 1,
            mb: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "12px" }} color="error">
            {error}
          </Typography>
          <CloseIcon
            sx={{ fontSize: "12px", color: "#f44336", cursor: "pointer" }}
            onClick={() => setError(undefined)}
          />
        </Box>
      )}
      {isListening && (
        <Box
          sx={{
            px: 1,
            mb: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "12px" }} color="primary">
            Recording... Click on the pause button to stop recording
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <TextField
          id="outlined-multiline-flexible"
          multiline
          maxRows={8}
          minRows={1}
          className="chat_input"
          placeholder="Type Something . . ."
          value={transcript}
          onChange={typingHandler}
          autoFocus
          disabled={typing || chatType.type === ""}
          hiddenLabel
          fullWidth
          size="small"
          sx={{
            background:
              theme.palette.mode === "dark" ? "#000000d6" : "#ffffffd6",
            "& .MuiInputBase-input::placeholder": {
              color: theme.palette.mode === "dark" ? "#aaa" : "#333",
              opacity: 1,
            },
          }}
          variant="outlined"
          onKeyDown={(e) => {
            if (e.shiftKey && e.key === "Enter") return;
            if (e.key === "Enter") sendMessage(e);
          }}
        />
        <Box sx={{ ml: 2, display: "flex", alignItems: "center" }}>
          <SendIcon
            color="primary"
            sx={{ fontSize: "25px", mr: 1, cursor: "pointer" }}
            onClick={() => sendMessage("", true)}
          />
          {isListening ? (
            <StopCircleIcon
              onClick={stopListening}
              color="primary"
              sx={{ fontSize: "35px", cursor: "pointer" }}
            />
          ) : (
            <MicNoneIcon
              id="micicon"
              onClick={startListening}
              color="primary"
              sx={{ fontSize: "35px", cursor: "pointer" }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInput;
